import type { YoutubeMatch, YoutubeVideo } from './api.js';
import type { AudioSelection } from './types.js';

/**
 * "Tvoja pesma": the sound step opening on the song the shopper already picked
 * in the product's Spotify question.
 *
 * A singing product that also prints a Spotify code asks for the song twice
 * unless the sound step remembers it. Spotify never hands out the audio, so
 * the step offers the same recording found on YouTube: one "Da" and the whole
 * song is in the arrangement, ready to cut like any YouTube pick.
 *
 * The decisions live here, away from the DOM, so they can be tested: when the
 * card shows, what it says about length, and what each match answer leads to.
 */

/** The song picked in the product's Spotify question, as the sound step sees it. */
export interface PickedSong {
  title: string;
  artists: string;
  uri?: string;
  durationMs?: number;
  /** The sleeve, for the card. */
  cover?: string;
}

export const OFFER_TEXTS = {
  question: 'Da li da proizvod pušta ovu pesmu?',
  yes: 'Da, pusti ovu pesmu',
  no: 'Ne, izaberi drugi zvuk',
  /** Right after "Da", before YouTube answered: the name is already the answer. */
  added: '✓ Pesma je dodata — cela pesma. Možeš odmah dalje.',
  /** A quiet line under it while the video is looked up (usually already found ahead). */
  looking: 'Tražimo je na YouTube-u da možeš da je poslušaš…',
  /** Not sure which upload it is: an optional refinement, never a gate. */
  choose: 'Koja je prava verzija? Izaberi je da je poslušaš ili iseciš — ili samo idi dalje, naziv je dovoljan.',
  /** The same list when the whole song does not fit: the card above says to cut it, so no "idi dalje". */
  chooseShort: 'Koja je prava verzija?',
  sameLength: 'ista dužina kao na Spotify-u',
  /** Offered among the tiles after "Ne", for a shopper who changes their mind. */
  again: (title: string) => `Koristi pesmu sa Spotify-a: ${title}`,
  premium: (length: string, base: string, price: string) =>
    `✨ Cela pesma traje ${length} — sa njom zvuk prelazi ${base}, pa se uključuje Premium dužina (+${price}).`,
  over: (max: string) => `Sa celom pesmom zvuk je duži od ${max} — posle izbora biraš koji deo ulazi.`,
};

/** Within this many milliseconds a YouTube video is the Spotify recording's length. */
const SAME_LENGTH_MS = 8_000;

function fold(text: string | undefined): string {
  return (text ?? '')
    .toLowerCase()
    .replace(/đ/g, 'dj')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** What "Ne" is remembered by: the track, so another song asks again. */
export function songKey(song: PickedSong): string {
  return song.uri || `${fold(song.title)}|${fold(song.artists)}`;
}

/**
 * Whether the song is already one of the sounds. By the Spotify id, or — for a
 * sound restored from the cart, which keeps its title and artist but not the
 * id — by the words the card wrote.
 */
export function songInMix(song: PickedSong, sels: AudioSelection[]): boolean {
  return sels.some(
    (sel) =>
      sel.kind === 'youtube' &&
      ((song.uri !== undefined && sel.spotifyUri === song.uri) ||
        (fold(sel.title) !== '' && fold(sel.title) === fold(song.title) && fold(sel.artist) === fold(song.artists))),
  );
}

/** Whether the "Tvoja pesma" card is the step's first screen right now. */
export function offerVisible(state: {
  enabled: boolean;
  song: PickedSong | null;
  sels: AudioSelection[];
  declined: ReadonlySet<string>;
}): boolean {
  const { song } = state;
  if (!state.enabled || !song || !song.title.trim()) return false;
  return !state.declined.has(songKey(song)) && !songInMix(song, state.sels);
}

/** The Premium or too-long line under the card, or null when the song simply fits. */
export function offerLengthNote(
  song: PickedSong,
  withSong: { premium: boolean; over: boolean },
  rules: { baseMs: number; limitMs: number; premium: { price: number } | null },
  price: (amount: number) => string,
): string | null {
  if (!song.durationMs) return null;
  if (withSong.over) return OFFER_TEXTS.over(fmtMs(rules.limitMs));
  if (withSong.premium && rules.premium) {
    return OFFER_TEXTS.premium(fmtMs(song.durationMs), fmtMs(rules.baseMs), price(rules.premium.price));
  }
  return null;
}

export type MatchPlan =
  /** Sure: the video goes straight into the YouTube editor. */
  | { take: YoutubeVideo }
  /** Unsure: "Koja je prava verzija?" with these, best first. */
  | { choose: YoutubeVideo[]; query: string }
  /** Nothing usable: the ordinary search, filled in with the song. */
  | { search: string; code?: string };

/** What the step does with the server's match. */
export function matchPlan(result: YoutubeMatch, song: PickedSong): MatchPlan {
  const query = result.query || [song.title, song.artists.split(',')[0]?.trim()].filter(Boolean).join(' ');
  if (result.video && result.confidence === 'high') return { take: result.video };
  if (result.candidates.length > 0) return { choose: result.candidates, query };
  return { search: query, ...(result.code ? { code: result.code } : {}) };
}

/** Whether a candidate runs as long as the Spotify recording. */
export function sameLength(video: YoutubeVideo, song: PickedSong): boolean {
  return Boolean(song.durationMs && video.durationMs && Math.abs(video.durationMs - song.durationMs) <= SAME_LENGTH_MS);
}

/**
 * The sound "Da" writes at once, before YouTube is asked anything: the whole
 * song by Spotify's own name, artist, id and length. That alone is a valid
 * answer — the worker searches "title artist" after the order — and its length
 * prices Premium right away. A video found later replaces it in place.
 */
export function songAnswer(song: PickedSong): Partial<AudioSelection> {
  return {
    url: undefined,
    storageKey: undefined,
    previewUrl: undefined,
    title: song.title,
    artist: song.artists || undefined,
    spotifyUri: song.uri,
    durationMs: song.durationMs,
    fromMs: undefined,
    toMs: undefined,
  };
}

/**
 * "Tvoja pesma", asked ahead. The sound control is built with the form, so it
 * hears the Spotify pick while the shopper is still on that step: the song is
 * matched on YouTube a moment after they stop changing it (`delayMs`), once
 * per song (`songKey`), and the server starts fetching a sure match right then.
 * "Da" then takes the answer that is already here or on its way.
 */
export interface MatchPrefetch {
  /** A song was picked (or cleared): it is matched after the delay, unless another replaces it first. */
  schedule(song: PickedSong | null): void;
  /**
   * This song's match: the one asked ahead, else asked now. One that came back
   * empty for a passing reason (busy, rate limited) is asked once more.
   */
  get(song: PickedSong): Promise<YoutubeMatch>;
  /** Drops a match not asked yet. */
  cancel(): void;
}

/** Why a match may be worth asking again: nothing was wrong with the song itself. */
const passing = (result: YoutubeMatch): boolean => result.code === 'unavailable' || result.code === 'rate_limited';

export function createMatchPrefetch(
  match: (song: PickedSong) => Promise<YoutubeMatch>,
  options: { delayMs?: number } = {},
): MatchPrefetch {
  const delayMs = options.delayMs ?? 500;
  const asked = new Map<string, { promise: Promise<YoutubeMatch>; retried: boolean }>();
  let timer: ReturnType<typeof setTimeout> | undefined;

  const ask = (song: PickedSong): { promise: Promise<YoutubeMatch>; retried: boolean } => {
    const key = songKey(song);
    let entry = asked.get(key);
    if (!entry) {
      entry = { promise: match(song).catch((): YoutubeMatch => ({ candidates: [], code: 'unavailable' })), retried: false };
      asked.set(key, entry);
      // A handful of songs is all one visit picks; the oldest go first.
      while (asked.size > 12) asked.delete(asked.keys().next().value!);
    }
    return entry;
  };

  const cancel = (): void => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  };

  return {
    schedule: (song) => {
      cancel();
      if (!song || !song.title.trim() || asked.has(songKey(song))) return;
      timer = setTimeout(() => {
        timer = undefined;
        ask(song);
      }, delayMs);
    },
    get: (song) => {
      cancel();
      const entry = ask(song);
      return entry.promise.then((result) => {
        if (!passing(result) || entry.retried) return result;
        entry.retried = true;
        entry.promise = match(song).catch((): YoutubeMatch => ({ candidates: [], code: 'unavailable' }));
        return entry.promise;
      });
    },
    cancel,
  };
}

function fmtMs(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

export { fmtMs as offerTime };
